import type { AxiosRequestConfig } from "axios";
import { getApiClient } from "./apiClient";

/** Root-scoped requests are addressed at `{apiBaseUrl}/root/...`. */
const ROOT_PREFIX = "/root";

/**
 * Custom request function used by the orval-generated client (configured as the
 * `mutator` in `orval.config.ts`).
 *
 * Every generated hook routes through the app-wide {@link getApiClient API
 * client}, which attaches the bearer token, resolves the tenant/root base URL
 * and handles 401s. Generated paths are scope-relative: a path starting with
 * `/root` targets the root scope (the prefix is stripped and forwarded with the
 * `isRootRequest` flag), everything else is tenant-scoped. The axios response is
 * unwrapped to its `data` so hooks resolve directly to the typed payload.
 */
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const client = getApiClient();
  const merged: AxiosRequestConfig = { ...config, ...options };
  const url = merged.url ?? "";

  if (url === ROOT_PREFIX || url.startsWith(`${ROOT_PREFIX}/`)) {
    return client<T>({
      ...merged,
      url: url.slice(ROOT_PREFIX.length) || "/",
      isRootRequest: true,
    }).then((response) => response.data);
  }

  return client<T>(merged).then((response) => response.data);
};
